import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import axios from 'axios';
import Layout from '@/components/Layout';
import HomePage from '@/pages/HomePage';
import BookingPage from '@/pages/BookingPage';
import StylesPage from '@/pages/StylesPage';
import GalleryPage from '@/pages/GalleryPage';
import SalonPage from '@/pages/SalonPage';
import ProfilePage from '@/pages/ProfilePage';
import LoyaltyPage from '@/pages/LoyaltyPage';
import ClientRegister from '@/pages/ClientRegister';
import ClientLogin from '@/pages/ClientLogin';
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminDashboardMenu from '@/pages/admin/AdminDashboardMenu';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ClientAuthProvider, useClientAuth } from '@/context/ClientAuthContext';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const themePresets = {
  'dark-gold': {
    '--background': '0 0% 6%', '--foreground': '0 0% 98%', '--card': '0 0% 10%',
    '--card-foreground': '0 0% 98%', '--secondary': '0 0% 14%', '--secondary-foreground': '0 0% 98%',
    '--accent': '41 58% 55%', '--accent-foreground': '0 0% 6%',
    '--muted': '0 0% 20%', '--muted-foreground': '0 0% 64%', '--border': '0 0% 18%',
  },
  'dark-silver': {
    '--background': '0 0% 6%', '--foreground': '0 0% 98%', '--card': '0 0% 10%',
    '--card-foreground': '0 0% 98%', '--secondary': '0 0% 14%', '--secondary-foreground': '0 0% 98%',
    '--accent': '0 0% 63%', '--accent-foreground': '0 0% 6%',
    '--muted': '0 0% 20%', '--muted-foreground': '0 0% 55%', '--border': '0 0% 18%',
  },
  'navy-gold': {
    '--background': '220 55% 10%', '--foreground': '220 20% 93%', '--card': '220 45% 15%',
    '--card-foreground': '220 20% 93%', '--secondary': '220 40% 20%', '--secondary-foreground': '220 20% 93%',
    '--accent': '41 58% 55%', '--accent-foreground': '220 55% 10%',
    '--muted': '220 30% 25%', '--muted-foreground': '220 15% 60%', '--border': '220 35% 22%',
  },
  'dark-green-gold': {
    '--background': '140 35% 7%', '--foreground': '140 15% 93%', '--card': '140 30% 11%',
    '--card-foreground': '140 15% 93%', '--secondary': '140 25% 15%', '--secondary-foreground': '140 15% 93%',
    '--accent': '45 50% 54%', '--accent-foreground': '140 35% 7%',
    '--muted': '140 20% 20%', '--muted-foreground': '140 10% 58%', '--border': '140 25% 17%',
  },
};

const ProtectedAdminRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin h-8 w-8 border-2 border-accent border-t-transparent rounded-full" /></div>;
  }
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const ProtectedClientRoute = ({ children }) => {
  const { isAuthenticated: clientAuth, loading: clientLoading } = useClientAuth();
  const { isAuthenticated: adminAuth } = useAuth();
  
  if (clientLoading) {
    return <div className="min-h-screen flex items-center justify-center">Caricamento...</div>;
  }
  
  return (clientAuth || adminAuth) ? children : <Navigate to="/login" />;
};

function AppContent() {
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/site-texts`).then(res => {
      const theme = res.data?.theme;
      if (theme && themePresets[theme]) {
        const root = document.documentElement;
        Object.entries(themePresets[theme]).forEach(([key, value]) => {
          root.style.setProperty(key, value);
        });
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <Routes>
          {/* Public Auth Routes */}
          <Route path="/register" element={<ClientRegister />} />
          <Route path="/login" element={<ClientLogin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          
          {/* All Routes under Layout */}
          <Route path="/" element={<Layout />}>
            <Route index element={<ProtectedClientRoute><HomePage /></ProtectedClientRoute>} />
            <Route path="prenota" element={<ProtectedClientRoute><BookingPage /></ProtectedClientRoute>} />
            <Route path="stili" element={<StylesPage />} />
            <Route path="galleria" element={<GalleryPage />} />
            <Route path="salone" element={<SalonPage />} />
            <Route path="profilo" element={<ProtectedClientRoute><ProfilePage /></ProtectedClientRoute>} />
            <Route path="fedelta" element={<ProtectedClientRoute><LoyaltyPage /></ProtectedClientRoute>} />
            
            {/* Admin Dashboard - Single page with tabs */}
            <Route path="admin" element={<ProtectedAdminRoute><AdminDashboardMenu /></ProtectedAdminRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ClientAuthProvider>
        <AppContent />
      </ClientAuthProvider>
    </AuthProvider>
  );
}

export default App;
